<template>
	<div class="rounded-lg border border-gray-200 overflow-hidden">
		<header class="p-4 border-b border-gray-200 bg-gray-50">
			<div class="flex items-start justify-between gap-3">
				<div>
					<p class="text-sm text-gray-600 mt-1">Assessment result summary</p>
				</div>
				<div
					class="px-3 py-1 rounded-full text-sm font-semibold"
					:class="isPassed ? 'bg-success-100 text-success-800' : 'bg-danger-100 text-danger-800'"
				>
					{{ isPassed ? 'Passed' : 'Failed' }}
				</div>
			</div>
		</header>

		<section class="p-4 space-y-4">
			<div class="grid grid-cols-1 md:grid-cols-5 gap-3">
				<div class="rounded-md border border-gray-200 p-3">
					<div class="text-xs text-gray-500 uppercase">Score</div>
					<div class="text-xl font-semibold text-gray-900 mt-1">{{ percentage }}%</div>
				</div>
				<div class="rounded-md border border-gray-200 p-3">
					<div class="text-xs text-gray-500 uppercase">Correct</div>
					<div class="text-xl font-semibold text-gray-900 mt-1">{{ correctAnswers }} / {{ totalQuestions }}</div>
				</div>
				<div class="rounded-md border border-gray-200 p-3">
					<div class="text-xs text-gray-500 uppercase">Points</div>
					<div class="text-xl font-semibold text-gray-900 mt-1">{{ formatPoints(earnedPoints) }} / {{ formatPoints(totalPoints) }}</div>
				</div>
				<div class="rounded-md border border-gray-200 p-3">
					<div class="text-xs text-gray-500 uppercase">Duration</div>
					<div class="text-xl font-semibold text-gray-900 mt-1">{{ formattedDuration }}</div>
				</div>
				<div class="rounded-md border border-gray-200 p-3">
					<div class="text-xs text-gray-500 uppercase">Retakes</div>
					<div class="text-xl font-semibold text-gray-900 mt-1">{{ retakeCount }}</div>
				</div>
			</div>

			<div>
				<div class="flex items-center justify-between text-xs text-gray-600 mb-1">
					<span>Passing Score: {{ passingPercent }}%</span>
				</div>
				<div class="h-2 bg-gray-200 rounded-full overflow-hidden">
					<div class="h-2 transition-all duration-300" :class="isPassed ? 'bg-success' : 'bg-danger'" :style="{ width: `${percentage}%` }"></div>
				</div>
			</div>

			<AssessmentRulesPanel :assessmentRules="assessmentRules" />

			<div class="rounded-md border border-gray-200 overflow-hidden">
				<div class="px-3 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold text-gray-700">
					Criteria-based Points
				</div>
				<div class="p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
					<div class="rounded-md border border-gray-200 overflow-hidden">
						<div class="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-semibold uppercase text-gray-600">
							Competency
						</div>
						<div class="p-2 space-y-1">
							<div
								v-for="item in displayCompetencyScores"
								:key="`comp-${item.label}`"
								class="rounded border border-gray-100 bg-gray-50/60 p-2"
							>
								<div class="flex items-center justify-between text-xs text-gray-700">
									<span class="truncate pr-2">{{ item.label }} ({{ item.questionCount }})</span>
									<span class="font-semibold whitespace-nowrap">{{ formatPoints(item.earnedPoints) }} / {{ formatPoints(item.totalPoints) }}</span>
								</div>
								<div class="mt-1 flex items-center justify-between text-[11px] text-gray-500">
									<span>Correct: {{ item.correctAnswers }} / {{ item.questionCount }}</span>
									<span>{{ item.scorePercent }}%</span>
								</div>
								<div class="mt-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
									<div class="h-1.5 rounded-full transition-all duration-300" :class="getScoreColorClass(item.scorePercent)" :style="{ width: `${item.scorePercent}%` }"></div>
								</div>
								<div
									v-if="getCompetencyThresholdInfo(item.label, item.scorePercent)"
									class="mt-1 flex items-center justify-between text-[11px]"
								>
									<span class="text-gray-500">
										Threshold: {{ getCompetencyThresholdInfo(item.label, item.scorePercent)?.threshold }}%
									</span>
									<span
										class="font-semibold"
										:class="getCompetencyThresholdInfo(item.label, item.scorePercent)?.met ? 'text-green-700' : 'text-amber-700'"
									>
										{{ getCompetencyThresholdInfo(item.label, item.scorePercent)?.met ? 'Met' : 'Below' }}
									</span>
								</div>
							</div>
							<div v-if="displayCompetencyScores.length === 0" class="text-xs text-gray-500">No data</div>
						</div>
					</div>

					<div class="rounded-md border border-gray-200 overflow-hidden">
						<div class="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-semibold uppercase text-gray-600">
							Type of Question
						</div>
						<div class="p-2 space-y-1">
							<div
								v-for="item in displayQuestionTypeScores"
								:key="`type-${item.label}`"
								class="rounded border border-gray-100 bg-gray-50/60 p-2"
							>
								<div class="flex items-center justify-between text-xs text-gray-700">
									<span class="truncate pr-2">{{ item.label }} ({{ item.questionCount }})</span>
									<span class="font-semibold whitespace-nowrap">{{ formatPoints(item.earnedPoints) }} / {{ formatPoints(item.totalPoints) }}</span>
								</div>
								<div class="mt-1 flex items-center justify-between text-[11px] text-gray-500">
									<span>Correct: {{ item.correctAnswers }} / {{ item.questionCount }}</span>
									<span>{{ item.scorePercent }}%</span>
								</div>
								<div class="mt-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
									<div class="h-1.5 rounded-full transition-all duration-300" :class="getScoreColorClass(item.scorePercent)" :style="{ width: `${item.scorePercent}%` }"></div>
								</div>
							</div>
							<div v-if="displayQuestionTypeScores.length === 0" class="text-xs text-gray-500">No data</div>
						</div>
					</div>

					<div class="rounded-md border border-gray-200 overflow-hidden">
						<div class="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-semibold uppercase text-gray-600">
							Critical Level
						</div>
						<div class="p-2 space-y-1">
							<div
								v-for="item in displayCriticalLevelScores"
								:key="`critical-${item.label}`"
								class="rounded border border-gray-100 bg-gray-50/60 p-2"
							>
								<div class="flex items-center justify-between text-xs text-gray-700">
									<span class="truncate pr-2">{{ item.label }} ({{ item.questionCount }})</span>
									<span class="font-semibold whitespace-nowrap">{{ formatPoints(item.earnedPoints) }} / {{ formatPoints(item.totalPoints) }}</span>
								</div>
								<div class="mt-1 flex items-center justify-between text-[11px] text-gray-500">
									<span>Correct: {{ item.correctAnswers }} / {{ item.questionCount }}</span>
									<span>{{ item.scorePercent }}%</span>
								</div>
								<div class="mt-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
									<div class="h-1.5 rounded-full transition-all duration-300" :class="getScoreColorClass(item.scorePercent)" :style="{ width: `${item.scorePercent}%` }"></div>
								</div>
							</div>
							<div v-if="displayCriticalLevelScores.length === 0" class="text-xs text-gray-500">No data</div>
						</div>
					</div>
				</div>
			</div>

			<div class="rounded-md border border-gray-200 overflow-hidden">
				<div class="px-3 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold text-gray-700">
					Attempt History
				</div>
				<div class="p-3 space-y-2">
					<div class="text-xs text-gray-600">
						Total attempts: <strong>{{ attemptHistory.length }}</strong> | Retakes: <strong>{{ retakeCount }}</strong>
					</div>
					<div class="max-h-[180px] overflow-y-auto border border-gray-200 rounded">
						<div
							v-for="attempt in attemptHistory"
							:key="`attempt-${attempt.attempt}`"
							class="px-3 py-2 border-b border-gray-100 last:border-b-0 text-xs text-gray-700 flex items-center justify-between gap-3"
						>
							<div class="font-semibold">Attempt {{ attempt.attempt }}</div>
							<div>Score: {{ attempt.percentage }}%</div>
							<div>Points: {{ formatPoints(attempt.earnedPoints) }} / {{ formatPoints(attempt.totalPoints) }}</div>
							<div>Correct: {{ attempt.correctAnswers }} / {{ attempt.totalQuestions }}</div>
							<div>{{ formatDuration(attempt.elapsedSeconds) }}</div>
						</div>
						<div v-if="attemptHistory.length === 0" class="px-3 py-2 text-xs text-gray-500">No attempts yet</div>
					</div>
				</div>
			</div>

			<div class="rounded-md border border-gray-200 overflow-hidden">
				<div class="px-3 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold text-gray-700">
					Question Breakdown
				</div>
				<div class="max-h-[280px] overflow-y-auto">
					<div
						v-for="result in results"
						:key="result.questionNumber"
						class="px-3 py-3 border-b border-gray-100 last:border-b-0"
					>
						<div class="flex items-start justify-between gap-3">
							<div>
								<div class="text-sm font-semibold text-gray-800">
									Q{{ result.questionNumber }}. {{ result.questionText }}
								</div>
								<div class="text-xs text-gray-600 mt-1">
									<strong>Selected: {{ formatAnswerLabels(result.selectedAnswerLabels, 'No answer') }}</strong>
								</div>
								<div class="text-xs text-gray-600 mt-1">
									Correct: {{ formatAnswerLabels(result.correctAnswerLabels, 'No correct answer marked') }}
								</div>
								<div v-if="resultCriteriaLine(result)" class="text-xs text-gray-600 mt-1">
									<strong>{{ resultCriteriaLine(result) }}</strong>
								</div>
								<div v-if="result.explanation" class="text-xs text-gray-600 mt-1">
									{{ result.explanation }}
								</div>
							</div>
							<div class="text-right shrink-0">
								<div
									class="text-xs font-semibold px-2 py-1 rounded-full"
									:class="result.isCorrect ? 'bg-success-100 text-success-800' : 'bg-danger-100 text-danger-800'"
								>
									{{ result.isCorrect ? 'Correct' : 'Incorrect' }}
								</div>
								<div class="text-xs text-gray-600 mt-2">
									{{ formatPoints(result.pointsEarned) }} / {{ formatPoints(result.pointsPossible) }} pts
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>

		<footer class="p-4 border-t border-gray-200 flex justify-end gap-2">
			<EwButton label="Retake" type="secondary" icon="tabler:rotate-clockwise" @click="$emit('retake')" />
			<EwButton label="Close" type="success" iconAppend="tabler:check" @click="$emit('close')" />
		</footer>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import AssessmentRulesPanel from './AssessmentRulesPanel.vue';

type EvaluationResult = {
	questionNumber: number;
	questionText: string;
	isCorrect: boolean;
	basePoints: number;
	pointsEarned: number;
	pointsPossible: number;
	competency: string;
	questionType: string;
	criticalLevel: string;
	competencyWeight: number;
	questionTypeWeight: number;
	criticalLevelWeight: number;
	totalWeightMultiplier: number;
	selectedAnswerLabels: string[];
	correctAnswerLabels: string[];
	explanation: string;
	rawCompetency: string;
	rawQuestionType: string;
	rawCriticality: string;
	pointsThreshold: string;
	questionFeedback: string;
	rawIllustrationImageSrc: string;
	rawImageSrc: string;
};

type AssessmentRules = {
	scoring?: Record<string, number | string | null | undefined> & {
		totalScore?: number | string;
		totalQuestions?: number | string;
		pointsPerQuestion?: number | string;
		passingScorePercent?: number | string;
	};
	thresholds?: Record<string, number | string | null>;
	Thresholds?: Record<string, number | string | null>;
	criticalRule?: number | string;
	distribution?: Record<string, number | string | null>;
	Distribution?: Record<string, number | string | null>;
	questionDistribution?: Record<string, number | string | null>;
	question_distribution?: Record<string, number | string | null>;
	'Question Distribution'?: Record<string, number | string | null>;
};

type CriteriaScoreItem = {
	label: string;
	earnedPoints: number;
	totalPoints: number;
	questionCount: number;
	correctAnswers: number;
	scorePercent: number;
};

type AttemptHistoryItem = {
	attempt: number;
	percentage: number;
	earnedPoints: number;
	totalPoints: number;
	correctAnswers: number;
	totalQuestions: number;
	elapsedSeconds: number;
	createdAt: string;
};

const props = defineProps<{
	title: string;
	totalQuestions: number;
	answeredQuestions: number;
	correctAnswers: number;
	totalPoints: number;
	earnedPoints: number;
	percentage: number;
	elapsedSeconds: number;
	passingPercent: number;
	results: EvaluationResult[];
	competencyScores: CriteriaScoreItem[];
	questionTypeScores: CriteriaScoreItem[];
	criticalLevelScores: CriteriaScoreItem[];
	attemptHistory: AttemptHistoryItem[];
	retakeCount: number;
	assessmentRules?: AssessmentRules | null;
}>();

defineEmits<{
	retake: [];
	close: [];
}>();

const isPassed = computed(() => props.percentage >= props.passingPercent);

const formattedDuration = computed(() => {
	const minutes = Math.floor(props.elapsedSeconds / 60)
		.toString()
		.padStart(2, '0');
	const seconds = Math.floor(props.elapsedSeconds % 60)
		.toString()
		.padStart(2, '0');
	return `${minutes}:${seconds}`;
});

const formatDuration = (elapsed: number): string => {
	const minutes = Math.floor(elapsed / 60)
		.toString()
		.padStart(2, '0');
	const seconds = Math.floor(elapsed % 60)
		.toString()
		.padStart(2, '0');
	return `${minutes}:${seconds}`;
};

const formatPoints = (value: number): string => {
	if (!Number.isFinite(value)) {
		return '0';
	}
	return Number.isInteger(value) ? String(value) : value.toFixed(2);
};

const hasValue = (value: unknown): boolean => String(value ?? '').trim().length > 0;

const toPlainText = (value: unknown): string => String(value ?? '').trim();

const formatCriteriaDisplayValue = (value: unknown): string => {
	const text = toPlainText(value);
	if (!text) {
		return '';
	}
	return text
		.replace(/[_-]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
		.split(' ')
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
		.join(' ');
};

const normalizeCriteriaKey = (value: unknown): string =>
	String(value ?? '')
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '');

const COMPETENCY_THRESHOLD_ALIASES: Record<string, string[]> = {
	communication: ['crossaudiencecommunication'],
	understanding: ['caseunderstanding'],
	limitsescalation: ['rolelimitsescalation'],
	rolebasedaction: ['rolebasedcompetency']
};

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const getAssessmentRuleRecord = (aliases: string[]): Record<string, unknown> => {
	const rules = props.assessmentRules;
	if (!isObjectRecord(rules)) {
		return {};
	}

	for (const alias of aliases) {
		const section = (rules as Record<string, unknown>)[alias];
		if (isObjectRecord(section)) {
			return section;
		}
	}

	const normalizedAliases = new Set(aliases.map((alias) => normalizeCriteriaKey(alias)));
	for (const [key, value] of Object.entries(rules)) {
		if (normalizedAliases.has(normalizeCriteriaKey(key)) && isObjectRecord(value)) {
			return value;
		}
	}

	return {};
};

const competencyThresholdMap = computed(() => {
	const map = new Map<string, number>();
	const thresholds = getAssessmentRuleRecord(['Thresholds', 'thresholds']);
	if (!Object.keys(thresholds).length) {
		return map;
	}

	Object.entries(thresholds).forEach(([key, rawValue]) => {
		const normalizedKey = normalizeCriteriaKey(key);
		const numericValue = Number(rawValue);
		if (!normalizedKey || !Number.isFinite(numericValue)) {
			return;
		}
		map.set(normalizedKey, Math.max(0, Math.min(100, numericValue)));
	});

	return map;
});

const getCompetencyThreshold = (label: string): number | null => {
	const normalizedLabel = normalizeCriteriaKey(label);
	if (!normalizedLabel) {
		return null;
	}
	const directThreshold = competencyThresholdMap.value.get(normalizedLabel);
	if (directThreshold !== undefined) {
		return directThreshold;
	}

	for (const alias of COMPETENCY_THRESHOLD_ALIASES[normalizedLabel] ?? []) {
		const aliasThreshold = competencyThresholdMap.value.get(alias);
		if (aliasThreshold !== undefined) {
			return aliasThreshold;
		}
	}

	return null;
};

const getCompetencyThresholdInfo = (
	label: string,
	scorePercent: number
): { threshold: number; met: boolean } | null => {
	const threshold = getCompetencyThreshold(label);
	if (threshold === null) {
		return null;
	}
	return {
		threshold,
		met: scorePercent >= threshold
	};
};

const getScoreColorClass = (percent: number): string => {
	if (percent >= 75) {
		return 'bg-green-500';
	}
	if (percent >= 50) {
		return 'bg-amber-500';
	}
	return 'bg-red-500';
};

const normalizeAnswerLabel = (value: unknown): string => {
	const raw = String(value ?? '').trim();
	if (!raw) {
		return '';
	}
	return raw.replace(/^<\s*/, '').replace(/\s*>$/, '').trim();
};

const formatAnswerLabels = (values: string[], fallback: string): string => {
	const cleaned = values
		.map((value) => normalizeAnswerLabel(value))
		.filter((value) => value.length > 0);
	return cleaned.length ? cleaned.join(', ') : fallback;
};

const resultCriteriaLine = (result: EvaluationResult): string => {
	const parts: string[] = [];
	if (hasValue(result.rawCompetency)) {
		parts.push(`Competency: ${formatCriteriaDisplayValue(result.rawCompetency)}`);
	}
	if (hasValue(result.rawQuestionType)) {
		parts.push(`Type: ${formatCriteriaDisplayValue(result.rawQuestionType)}`);
	}
	if (hasValue(result.rawCriticality)) {
		parts.push(`Criticality: ${formatCriteriaDisplayValue(result.rawCriticality)}`);
	}
	if (hasValue(result.pointsThreshold)) {
		parts.push(`Points Threshold: ${result.pointsThreshold}`);
	}
	return parts.join(' | ');
};

const toCriteriaLabel = (value: unknown): string => formatCriteriaDisplayValue(value);

const buildCriteriaScoresFromResults = (
	items: EvaluationResult[],
	key: 'rawCompetency' | 'rawQuestionType' | 'rawCriticality'
): CriteriaScoreItem[] => {
	const scoreMap = new Map<string, CriteriaScoreItem>();

	items.forEach((item) => {
		const label = toCriteriaLabel(item[key]);
		if (!label) {
			return;
		}
		const existing = scoreMap.get(label) ?? {
			label,
			earnedPoints: 0,
			totalPoints: 0,
			questionCount: 0,
			correctAnswers: 0,
			scorePercent: 0
		};
		existing.earnedPoints += Number(item.pointsEarned ?? 0);
		existing.totalPoints += Number(item.pointsPossible ?? 0);
		existing.questionCount += 1;
		existing.correctAnswers += item.isCorrect ? 1 : 0;
		scoreMap.set(label, existing);
	});

	return Array.from(scoreMap.values())
		.map((item) => ({
			...item,
			earnedPoints: Number(item.earnedPoints.toFixed(2)),
			totalPoints: Number(item.totalPoints.toFixed(2)),
			scorePercent:
				item.totalPoints > 0
					? Number(((item.earnedPoints / item.totalPoints) * 100).toFixed(1))
					: 0
		}))
		.sort((a, b) => b.totalPoints - a.totalPoints);
};

const displayCompetencyScores = computed(() =>
	buildCriteriaScoresFromResults(props.results, 'rawCompetency')
);
const displayQuestionTypeScores = computed(() =>
	buildCriteriaScoresFromResults(props.results, 'rawQuestionType')
);
const displayCriticalLevelScores = computed(() =>
	buildCriteriaScoresFromResults(props.results, 'rawCriticality')
);
</script>
